import {
  type CelebrationIntensity,
  DEFAULT_APPOINTMENT_DURATION_MIN,
  DEFAULT_CELEBRATIONS,
  DEFAULT_HOURLY_RATE_EUR,
  DEFAULT_PROMPT_DELAY_MIN,
  DEFAULT_RATE_45_EUR,
  DEFAULT_RETROCESSION_RATE_PCT,
  MAX_HOURLY_RATE_EUR,
  MAX_PROMPT_DELAY_MIN,
  MAX_RETROCESSION_RATE_PCT,
  MIN_PROMPT_DELAY_MIN,
} from '@ergo/shared';
import { type FormEvent, useEffect, useState } from 'react';
import { api, errorMessage } from '../../api';
import { setCelebrationIntensity } from '../../celebrations';
import { useResource } from '../../hooks';

/** Deux tons, pas un curseur : elle choisit une ambiance, pas un nombre de cœurs. */
const CELEBRATION_CHOICES: { value: CelebrationIntensity; label: string }[] = [
  { value: 'douce', label: 'Douce' },
  { value: 'joueuse', label: 'Joueuse' },
];

export function SettingsForm() {
  const settings = useResource(() => api.settings.get(), []);
  const [promptDelayMin, setPromptDelayMin] = useState(DEFAULT_PROMPT_DELAY_MIN);
  const [defaultDurationMin, setDefaultDurationMin] = useState(DEFAULT_APPOINTMENT_DURATION_MIN);
  const [hourlyRateEur, setHourlyRateEur] = useState(DEFAULT_HOURLY_RATE_EUR);
  const [rate45Eur, setRate45Eur] = useState(DEFAULT_RATE_45_EUR);
  const [retrocessionRatePct, setRetrocessionRatePct] = useState(DEFAULT_RETROCESSION_RATE_PCT);
  const [celebrations, setCelebrations] = useState<CelebrationIntensity>(DEFAULT_CELEBRATIONS);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!settings.data) return;
    setPromptDelayMin(settings.data.promptDelayMin);
    setDefaultDurationMin(settings.data.defaultDurationMin);
    setHourlyRateEur(settings.data.hourlyRateEur);
    setRate45Eur(settings.data.rate45Eur);
    setRetrocessionRatePct(settings.data.retrocessionRatePct);
    setCelebrations(settings.data.celebrations);
  }, [settings.data]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    setSaved(false);
    try {
      await api.settings.update({
        promptDelayMin,
        defaultDurationMin,
        hourlyRateEur,
        rate45Eur,
        retrocessionRatePct,
        celebrations,
      });
      // Les fêtes lisent le réglage hors de React : il faut le leur redire tout de suite,
      // sans quoi le changement n'arriverait qu'au prochain chargement de l'app.
      setCelebrationIntensity(celebrations);
      setSaved(true);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="panel">
      <h2 className="panel__title">Valeurs par défaut</h2>

      {settings.error && (
        <p className="error" role="alert">
          {settings.error}
        </p>
      )}

      <form className="form" onSubmit={onSubmit}>
        <div className="field-row">
          <label className="field">
            <span className="field__label">Délai avant la notification (min)</span>
            <input
              className="input"
              type="number"
              min={MIN_PROMPT_DELAY_MIN}
              max={MAX_PROMPT_DELAY_MIN}
              step={1}
              value={promptDelayMin}
              onChange={(event) => setPromptDelayMin(Number(event.target.value))}
              required
            />
            <span className="hint">Après la fin prévue de la séance.</span>
          </label>

          <label className="field">
            <span className="field__label">Durée de séance par défaut (min)</span>
            <input
              className="input"
              type="number"
              min={5}
              max={600}
              step={5}
              value={defaultDurationMin}
              onChange={(event) => setDefaultDurationMin(Number(event.target.value))}
              required
            />
          </label>
        </div>

        <div className="field-row">
          <label className="field">
            <span className="field__label">Taux horaire</span>
            <span className="suffixed">
              <input
                className="input"
                type="number"
                min={0.5}
                max={MAX_HOURLY_RATE_EUR}
                step={0.5}
                value={hourlyRateEur}
                onChange={(event) => setHourlyRateEur(Number(event.target.value))}
                required
              />
              <span className="suffixed__unit">€</span>
            </span>
          </label>

          <label className="field">
            <span className="field__label">Tarif de la séance de 45 min</span>
            <span className="suffixed">
              <input
                className="input"
                type="number"
                min={0.5}
                max={MAX_HOURLY_RATE_EUR}
                step={0.5}
                value={rate45Eur}
                onChange={(event) => setRate45Eur(Number(event.target.value))}
                required
              />
              <span className="suffixed__unit">€</span>
            </span>
          </label>
        </div>
        <span className="hint">Les autres durées se calculent au taux horaire.</span>

        <label className="field">
          <span className="field__label">Rétrocession au cabinet</span>
          <span className="suffixed">
            <input
              className="input"
              type="number"
              min={0}
              max={MAX_RETROCESSION_RATE_PCT}
              step={0.5}
              value={retrocessionRatePct}
              onChange={(event) => setRetrocessionRatePct(Number(event.target.value))}
              required
            />
            <span className="suffixed__unit">%</span>
          </span>
          <span className="hint">
            Part des règlements des patients du cabinet reversée à la titulaire. 0 pour désactiver.
          </span>
        </label>

        <fieldset className="field field--fieldset">
          <legend className="field__label">Célébrations</legend>
          <div className="segmented">
            {CELEBRATION_CHOICES.map((choice) => (
              <label
                key={choice.value}
                className={`segmented__option${celebrations === choice.value ? ' is-active' : ''}`}
              >
                <input
                  className="visually-hidden"
                  type="radio"
                  name="celebrations"
                  value={choice.value}
                  checked={celebrations === choice.value}
                  onChange={() => setCelebrations(choice.value)}
                />
                {choice.label}
              </label>
            ))}
          </div>
          <span className="hint">
            Les cœurs et les pétales quand une étape est faite. « Douce » en met moins.
          </span>
        </fieldset>

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <div className="form__actions">
          <button className="button button--primary" type="submit" disabled={pending}>
            {pending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          {saved && <span className="muted">Enregistré.</span>}
        </div>
      </form>
    </section>
  );
}
